import React, { memo, useContext, useEffect, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTitle, spacer16 } from '@spotify-internal/encore-web';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { Accordion as ArticleAccordionProps, AccordionItem as AccordionItemType, PortableText } from 'lib/sanity/types';
import BlockSerializer from 'lib/sanity/ui/BlockSerializer';
import analytics from 'utils/client/analytics/analytics';
import { BlockSerializerUbiContext } from 'lib/sanity/ui/BlockSerializerUbiProvider';

// Memo stops an open accordion from rerender when another is expanded, making video iframes for example not getting refreshed.
const MemoizedAccordionContent = memo(({ content, isExpanded }: { content?: PortableText; isExpanded: boolean }) => (
  <AccordionContent isExpanded={isExpanded}>{content && <BlockSerializer value={content} />}</AccordionContent>
));

const StyledAccordion = styled(Accordion)`
  h2 + ol,
  h2 + ul,
  h3 + ol,
  h3 + ul {
    margin-block-start: 1em;
  }

  padding-bottom: 0;
  margin-bottom: ${spacer16};
  background: transparent;
`;

const trackAccordionToggle = (asPath: string, title: string) =>
  analytics.trackEvent({
    category: 'accordion',
    action: title,
    label: asPath,
  });
const ArticleAccordion = ({ accordion }: { accordion: ArticleAccordionProps }) => {
  const [isExpanded, setIsExpanded] = useState<{ [key: string]: boolean }>({});
  const { asPath } = useRouter();
  const ubi = useContext(BlockSerializerUbiContext);

  useEffect(() => {
    ubi.logAccordionImpression(accordion._key);
  }, [ubi, accordion._key]);

  if (!accordion.items.length) {
    return null;
  }
  const handleExpand = (accordionItem: AccordionItemType) => {
    const expanded = !isExpanded[accordionItem._key];
    ubi.logAccordionInteraction(accordion._key, expanded);
    setIsExpanded({
      ...isExpanded,
      [accordionItem._key]: expanded,
    });
    trackAccordionToggle(asPath, accordionItem.title);
  };

  return (
    <StyledAccordion>
      {accordion.items.map(accordionItem => (
        <AccordionItem key={accordionItem._key}>
          <AccordionTitle
            onClick={() => handleExpand(accordionItem)}
            isExpanded={isExpanded[accordionItem._key]}
            aria-expanded={!!isExpanded[accordionItem._key]}
          >
            {accordionItem.title}
          </AccordionTitle>
          <MemoizedAccordionContent content={accordionItem.content} isExpanded={isExpanded[accordionItem._key]} />
        </AccordionItem>
      ))}
    </StyledAccordion>
  );
};

export default ArticleAccordion;
